import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markNotificationRead,
  dismissNotification,
  markAllNotificationsRead,
} from '../api';

const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];
const NOTIFICATIONS_KEY = ['notifications', 'list'];

/**
 * Hook for notification center.
 *
 * - Polls unread count every 60s (lightweight)
 * - Full list only fetched on demand (when panel opens)
 */
export function useNotifications() {
  const queryClient = useQueryClient();

  // Unread count - polled every 60s
  const { data: unreadData } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: fetchUnreadNotificationCount,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Full notification list - fetched on demand
  const {
    data: notifications,
    refetch: refetchNotifications,
    isLoading: isLoadingNotifications,
  } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: fetchNotifications,
    enabled: false, // Only fetch when panel opens
    staleTime: 10000,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidateAll,
  });

  const dismiss = useMutation({
    mutationFn: dismissNotification,
    onSuccess: invalidateAll,
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateAll,
  });

  return {
    unreadCount: unreadData?.unread_count ?? 0,
    notifications: notifications ?? [],
    isLoadingNotifications,
    refetchNotifications,
    markRead: markRead.mutate,
    dismiss: dismiss.mutate,
    markAllRead: markAllRead.mutate,
  };
}
